import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Paper,
  Typography,
  Button,
  CircularProgress,
  Alert,
  Chip,
  LinearProgress,
  IconButton,
  TextField,
  Grid,
  Card,
  CardContent,
  Tooltip,
  Snackbar
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  Edit as EditIcon,
  Visibility as PreviewIcon,
  Share as ShareIcon,
  CheckCircle as CompletedIcon,
  RadioButtonUnchecked as NotStartedIcon,
  HourglassEmpty as InProgressIcon,
  Download as DownloadIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  PhotoCamera as PhotoIcon,
  Work as WorkIcon,
  Info as InfoIcon
} from '@mui/icons-material';
import { getStorage, ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { getFirestore, collection, query, where, orderBy, limit, getDocs } from 'firebase/firestore';
import { auth } from '../../firebase';
import { getProfile, updateProfile, updateTrainingPlanItem, updatePhone } from '../../utils/profileService';
import { usePlatformSettings } from '../../hooks/usePlatformSettings';
import LockIcon from '@mui/icons-material/Lock';
import GeneratingProgress from './GeneratingProgress';
import PhonePromptModal from './PhonePromptModal';

function Profile() {
  const { id: profileId } = useParams();
  const navigate = useNavigate();
  const { settings: platformSettings } = usePlatformSettings();

  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState(null);
  const [isOwner, setIsOwner] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [showAllTrainingItems, setShowAllTrainingItems] = useState(false);
  const [showAllWorkHistory, setShowAllWorkHistory] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [showBackgroundSections, setShowBackgroundSections] = useState(false);
  const [pollCount, setPollCount] = useState(0);
  const [showPhoneModal, setShowPhoneModal] = useState(false);
  const [generatingStartTime, setGeneratingStartTime] = useState(null);
  
  // Cognitive profile data
  const [cognitiveData, setCognitiveData] = useState(null);
  const [loadingCognitive, setLoadingCognitive] = useState(true);

  // Editable fields
  const [editedFields, setEditedFields] = useState({
    currentRole: '',
    targetRole: '',
    yearsExperience: 0,
    skills: []
  });
  const [skillInput, setSkillInput] = useState('');

  // Load profile
  useEffect(() => {
    loadProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profileId]);

  // Load cognitive data for the profile owner
  useEffect(() => {
    const loadCognitiveData = async () => {
      try {
        // Extract userId from profileId (format: {userId}_career)
        const userId = profileId?.split('_')[0];
        if (!userId) {
          console.log('🧠 No userId found in profileId');
          setLoadingCognitive(false);
          return;
        }

        console.log('🧠 Loading cognitive data for userId:', userId);
        const db = getFirestore();
        const forecastsRef = collection(db, 'forecasts');
        
        // Get all forecasts for this user to avoid index issues
        const userForecastsQuery = query(
          forecastsRef,
          where('userId', '==', userId)
        );
        
        const snapshot = await getDocs(userForecastsQuery);
        console.log('🧠 Found', snapshot.size, 'forecasts for user');
        
        if (snapshot.empty) {
          console.log('🧠 No forecasts found for user');
          setCognitiveData({ hasReport: false });
          setLoadingCognitive(false);
          return;
        }
        
        // Convert to array and find the best forecast
        const allForecasts = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        
        console.log('🧠 All forecasts:', allForecasts.map(f => ({
          id: f.id,
          status: f.status,
          isActive: f.isActive,
          hasAiAnalysis: !!f.aiAnalysis,
          hasReportData: !!f.reportData
        })));
        
        // Priority: 1) Active forecast, 2) Most recent completed forecast
        let forecastData = allForecasts.find(f => f.isActive === true);
        
        if (forecastData) {
          console.log('🧠 Found active forecast:', forecastData.id);
        } else {
          // Find most recent completed/analyzed forecast with data
          const completedForecasts = allForecasts.filter(f => 
            ['complete', 'completed', 'analyzed'].includes(f.status) && (f.aiAnalysis || f.reportData)
          );
          
          if (completedForecasts.length > 0) {
            // Sort by createdAt descending
            completedForecasts.sort((a, b) => {
              const aTime = a.createdAt?.toDate?.() || new Date(a.createdAt) || new Date(0);
              const bTime = b.createdAt?.toDate?.() || new Date(b.createdAt) || new Date(0);
              return bTime - aTime;
            });
            forecastData = completedForecasts[0];
            console.log('🧠 Using most recent completed forecast:', forecastData.id);
          }
        }
        
        if (forecastData) {
          // Extract cognitive metrics - check aiAnalysis first (where it's actually stored), then reportData
          const aiAnalysis = forecastData.aiAnalysis || forecastData.reportData || {};
          const cogArchitecture = aiAnalysis.section2_cognitiveArchitecture;
          
          console.log('🧠 Forecast aiAnalysis keys:', Object.keys(aiAnalysis));
          
          if (cogArchitecture) {
            setCognitiveData({
              ces: cogArchitecture.cognitiveEfficiencyScore || {},
              cq: cogArchitecture.creativityQuotient || {},
              tier: cogArchitecture.cognitiveTier || {},
              hasReport: true
            });
            console.log('🧠 Cognitive data loaded successfully:', cogArchitecture);
          } else {
            console.log('🧠 Forecast found but no section2_cognitiveArchitecture in aiAnalysis');
            setCognitiveData({ hasReport: false });
          }
        } else {
          console.log('🧠 No completed forecasts found');
          setCognitiveData({ hasReport: false });
        }
      } catch (err) {
        console.error('🧠 Error loading cognitive data:', err);
        setCognitiveData({ hasReport: false });
      } finally {
        setLoadingCognitive(false);
      }
    };

    loadCognitiveData();
  }, [profileId]);

  // Polling effect for generating state
  useEffect(() => {
    if (!generating) return;
    
    const pollInterval = setInterval(() => {
      console.log('🔄 Polling for profile update... (attempt', pollCount + 1, ')');
      setPollCount(prev => prev + 1);
      loadProfile(true); // Silent reload
    }, 3000); // Poll every 3 seconds

    return () => clearInterval(pollInterval);
  }, [generating, pollCount, profileId]);

  const loadProfile = async (silent = false) => {
    try {
      if (!silent) setLoading(true);
      setError(null);
      
      const result = await getProfile(profileId);
      
      if (result.success) {
        console.log('📋 Profile loaded:', result.profile);
        console.log('📋 Training plan items count:', result.profile.trainingPlan?.items?.length);
        
        // Only show generating state if:
        // 1. Profile was created very recently (within last 2 minutes) AND
        // 2. No training plan items exist yet
        // This prevents showing generating state when switching active plans
        const createdAt = result.profile.createdAt?.toDate?.() || 
                         (result.profile.createdAt ? new Date(result.profile.createdAt) : null);
        const lastAnalyzed = result.profile.lastAnalyzedAt?.toDate?.() || 
                            (result.profile.lastAnalyzedAt ? new Date(result.profile.lastAnalyzedAt) : null);
        const relevantTime = lastAnalyzed || createdAt;
        const twoMinutesAgo = new Date(Date.now() - 2 * 60 * 1000);
        const isRecentlyCreated = relevantTime && relevantTime > twoMinutesAgo;
        
        const hasNoTrainingItems = !result.profile.trainingPlan?.items?.length;
        const isStillGenerating = hasNoTrainingItems && isRecentlyCreated && result.profile.targetRole;
        
        console.log('📋 Generating check:', { hasNoTrainingItems, isRecentlyCreated, isStillGenerating });
        
        setProfile(result.profile);
        setIsOwner(result.isOwner);
        setGenerating(isStillGenerating);
        
        // Track when generation started for progress display
        if (isStillGenerating && !generatingStartTime) {
          setGeneratingStartTime(Date.now());
        }
        
        if (!isStillGenerating) {
          setPollCount(0); // Reset poll count when done
          setGeneratingStartTime(null);
          
          // Show phone prompt if user doesn't have phone and this is a new report
          if (result.isOwner && !result.profile.hasPhone && isRecentlyCreated) {
            setTimeout(() => setShowPhoneModal(true), 1000);
          }
        }
        
        setEditedFields({
          currentRole: result.profile.currentRole || '',
          targetRole: result.profile.targetRole || '',
          yearsExperience: result.profile.yearsExperience || 0,
          skills: result.profile.skills || []
        });
      }
    } catch (err) {
      console.error('Error loading profile:', err);
      if (err.code === 'functions/not-found') {
        // Profile not created yet - might still be generating
        if (pollCount < 20) { // Max 20 polls (60 seconds)
          setGenerating(true);
          setError(null);
        } else {
          setError('Profile not found. Please try creating a new career analysis.');
          setGenerating(false);
        }
      } else {
        setError('Failed to load profile. Please try again.');
        setGenerating(false);
      }
    } finally {
      if (!silent) setLoading(false);
    }
  };

  // Handle save profile edits
  const handleSave = async () => {
    try {
      setSaving(true);
      await updateProfile(profileId, editedFields);
      setProfile(prev => ({ ...prev, ...editedFields }));
      setEditMode(false);
      setSnackbar({ open: true, message: 'Profile updated successfully', severity: 'success' });
    } catch (err) {
      console.error('Error saving profile:', err);
      setSnackbar({ open: true, message: 'Failed to save changes', severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  // Handle training item status change
  const handleTrainingItemStatusChange = async (itemId, newStatus) => {
    try {
      const result = await updateTrainingPlanItem(profileId, itemId, newStatus);
      
      // Update local state
      setProfile(prev => ({
        ...prev,
        trainingPlan: {
          ...prev.trainingPlan,
          items: prev.trainingPlan.items.map(item =>
            item.id === itemId ? { ...item, status: newStatus } : item
          ),
          summary: result.summary
        },
        // Add qualified role if milestone was achieved
        ...(result.milestoneAchieved && result.qualifiedRole ? {
          qualifiedRoles: [...(prev.qualifiedRoles || []), {
            role: result.qualifiedRole,
            completedAt: new Date().toISOString()
          }]
        } : {})
      }));
      
      // Show celebration message if milestone achieved
      if (result.milestoneAchieved) {
        setSnackbar({ 
          open: true, 
          message: '🎉 Congratulations! You\'ve qualified for this role!', 
          severity: 'success' 
        });
      } else {
        setSnackbar({ open: true, message: 'Progress updated', severity: 'success' });
      }
    } catch (err) {
      console.error('Error updating training item:', err);
      setSnackbar({ open: true, message: 'Failed to update progress', severity: 'error' });
    }
  };

  // Copy profile URL to clipboard
  const handleCopyLink = () => {
    const url = window.location.href;
    navigator.clipboard.writeText(url);
    setSnackbar({ open: true, message: 'Profile link copied to clipboard', severity: 'success' });
  };

  // Add skill
  const handleAddSkill = () => {
    if (skillInput.trim() && !editedFields.skills.includes(skillInput.trim())) {
      setEditedFields(prev => ({
        ...prev,
        skills: [...prev.skills, skillInput.trim()]
      }));
      setSkillInput('');
    }
  };

  // Remove skill
  const handleRemoveSkill = (skillToRemove) => {
    setEditedFields(prev => ({
      ...prev,
      skills: prev.skills.filter(s => s !== skillToRemove)
    }));
  };

  // Handle photo upload
  const handlePhotoUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type and size
    if (!file.type.startsWith('image/')) {
      setSnackbar({ open: true, message: 'Please select an image file', severity: 'error' });
      return;
    }
    if (file.size > 5 * 1024 * 1024) { // 5MB limit
      setSnackbar({ open: true, message: 'Image must be less than 5MB', severity: 'error' });
      return;
    }

    try {
      setUploadingPhoto(true);
      console.log('📷 Starting photo upload for profile:', profileId);
      
      const storage = getStorage();
      const photoRef = ref(storage, `profile-photos/${profileId}`);
      
      console.log('📷 Uploading to storage...');
      await uploadBytes(photoRef, file);
      
      console.log('📷 Getting download URL...');
      const downloadUrl = await getDownloadURL(photoRef);
      console.log('📷 Download URL:', downloadUrl);
      
      // Update profile with new photo URL
      console.log('📷 Saving photo URL to profile...');
      const result = await updateProfile(profileId, { photoUrl: downloadUrl });
      console.log('📷 Update result:', result);
      
      setProfile(prev => ({ ...prev, photoUrl: downloadUrl }));
      setSnackbar({ open: true, message: 'Photo updated successfully', severity: 'success' });
    } catch (err) {
      console.error('📷 Photo upload error:', err);
      console.error('📷 Error code:', err.code);
      console.error('📷 Error message:', err.message);
      console.error('Error uploading photo:', err);
      setSnackbar({ open: true, message: 'Failed to upload photo', severity: 'error' });
    } finally {
      setUploadingPhoto(false);
    }
  };

  // Get sorted training items (by order field)
  const getSortedTrainingItems = () => {
    const items = profile.trainingPlan?.items || [];
    return [...items].sort((a, b) => (a.order || 0) - (b.order || 0));
  };
  
  // Get training items to display (show all or collapsed)
  const getDisplayedTrainingItems = () => {
    const items = getSortedTrainingItems();
    if (showAllTrainingItems) return items;
    return items.slice(0, 5);
  };
  
  // Calculate training stats with smart subscription-aware cost estimation
  const getTrainingStats = () => {
    const items = profile.trainingPlan?.items || [];
    const certCount = items.filter(i => i.type === 'certification').length;
    const courseCount = items.filter(i => i.type === 'course').length;
    const completedCount = items.filter(i => i.status === 'completed').length;
    const remainingItems = items.filter(i => i.status !== 'completed');
    const remainingCertCount = remainingItems.filter(i => i.type === 'certification').length;
    
    // Subscription providers and their monthly costs
    const subscriptionProviders = {
      'coursera': 49,
      'linkedin learning': 30,
      'linkedin': 30,
      'pluralsight': 29,
      'a cloud guru': 35,
      'acloudguru': 35,
      'udacity': 399, // Per month for nanodegree
      'skillshare': 14,
      'datacamp': 25,
      'codecademy': 20,
      'treehouse': 25,
      'masterclass': 15
    };
    
    const isSubscriptionProvider = (provider) => {
      const providerLower = (provider || '').toLowerCase();
      for (const sub of Object.keys(subscriptionProviders)) {
        if (providerLower.includes(sub)) return sub;
      }
      return null;
    };
    
    // Extract hours from duration string
    const extractHours = (duration) => {
      if (!duration) return 0;
      const match = String(duration).match(/(\d+)\s*(?:hours?|hrs?)/i);
      return match ? parseInt(match[1]) : 0;
    };
    
    // Group items by provider type
    const subscriptionHours = {}; // { 'coursera': 85, 'linkedin': 5 }
    let oneTimeCosts = 0;
    let totalRemainingHours = 0;
    
    items.forEach(item => {
      const provider = item.provider || '';
      const cost = item.cost || 0;
      const hours = extractHours(item.duration);
      const subProvider = isSubscriptionProvider(provider);
      
      if (item.type === 'certification') {
        // Certification exam fees are one-time
        oneTimeCosts += cost;
      } else if (subProvider) {
        // Subscription provider - accumulate hours
        subscriptionHours[subProvider] = (subscriptionHours[subProvider] || 0) + hours;
      } else if (cost > 0) {
        // One-time purchase (e.g., Udemy course)
        oneTimeCosts += cost;
      }
      // Free items don't add to cost
      
      // Accumulate remaining hours for incomplete items
      if (item.status !== 'completed') {
        totalRemainingHours += hours;
      }
    });
    
    // Calculate subscription costs based on estimated months
    // Assume user studies 10-15 hours per week = ~50 hours/month
    const hoursPerMonth = 50;
    let subscriptionCost = 0;
    
    Object.entries(subscriptionHours).forEach(([provider, totalHours]) => {
      if (totalHours > 0) {
        const months = Math.ceil(totalHours / hoursPerMonth);
        const monthlyCost = subscriptionProviders[provider] || 0;
        subscriptionCost += months * monthlyCost;
      }
    });
    
    const totalCost = subscriptionCost + oneTimeCosts;
    
    // Calculate estimated time to completion
    // Parse hoursPerWeek from profile (format: "10-15" or "5-10" etc.)
    const hoursPerWeekStr = profile.trainingPlan?.hoursPerWeek || profile.hoursPerWeek || '10';
    let hoursPerWeek = 10; // default
    const weekMatch = String(hoursPerWeekStr).match(/(\d+)/);
    if (weekMatch) {
      hoursPerWeek = parseInt(weekMatch[1]);
    }
    
    // Calculate total hours for ALL items (not just remaining)
    let totalPlanHours = 0;
    items.forEach(item => {
      totalPlanHours += extractHours(item.duration);
    });
    // Add 2 weeks prep time per certification
    const totalCertCount = items.filter(i => i.type === 'certification').length;
    totalPlanHours += totalCertCount * (hoursPerWeek * 2);
    
    // Add 2 weeks (in hours equivalent) for each remaining certification for study/prep time
    const certPrepHours = remainingCertCount * (hoursPerWeek * 2);
    const totalTimeHours = totalRemainingHours + certPrepHours;
    
    // Calculate weeks to completion (baseline, without velocity adjustment)
    const baseWeeksToCompletion = hoursPerWeek > 0 ? Math.ceil(totalTimeHours / hoursPerWeek) : 0;
    
    // Calculate learning velocity
    // Velocity = (actual progress) / (expected progress based on time elapsed)
    let velocity = 1.0;
    let weeksElapsed = 0;
    
    const startDate = profile.lastAnalyzedAt?.toDate?.() || 
                     (profile.lastAnalyzedAt ? new Date(profile.lastAnalyzedAt._seconds * 1000) : null) ||
                     profile.createdAt?.toDate?.() ||
                     (profile.createdAt ? new Date(profile.createdAt._seconds * 1000) : null);
    
    if (startDate && completedCount > 0) {
      const now = new Date();
      const msElapsed = now - startDate;
      weeksElapsed = msElapsed / (1000 * 60 * 60 * 24 * 7);
      
      if (weeksElapsed >= 0.5) { // Need at least half a week of data
        // Expected progress based on baseline plan
        const expectedWeeksForFullPlan = hoursPerWeek > 0 ? totalPlanHours / hoursPerWeek : 52;
        const expectedProgressPercent = Math.min(100, (weeksElapsed / expectedWeeksForFullPlan) * 100);
        const actualProgressPercent = (completedCount / items.length) * 100;
        
        if (expectedProgressPercent > 0) {
          velocity = actualProgressPercent / expectedProgressPercent;
          // Cap velocity between 0.25x and 3x for reasonable display
          velocity = Math.max(0.25, Math.min(3, velocity));
        }
      }
    }
    
    // Adjust weeks to completion based on velocity
    const adjustedWeeksToCompletion = velocity > 0 ? Math.ceil(baseWeeksToCompletion / velocity) : baseWeeksToCompletion;
    
    // Calculate projected hire-ready date
    const projectedDate = new Date();
    projectedDate.setDate(projectedDate.getDate() + (adjustedWeeksToCompletion * 7));
    
    // Format as month range (current month + 1 month buffer)
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const startMonth = monthNames[projectedDate.getMonth()];
    const endDate = new Date(projectedDate);
    endDate.setMonth(endDate.getMonth() + 1);
    const endMonth = monthNames[endDate.getMonth()];
    const hireReadyWindow = startMonth === endMonth ? startMonth : `${startMonth}-${endMonth}`;
    
    // Format as readable string
    let estimatedTime = '';
    if (adjustedWeeksToCompletion === 0) {
      estimatedTime = 'Complete!';
    } else if (adjustedWeeksToCompletion <= 4) {
      estimatedTime = `~${adjustedWeeksToCompletion} week${adjustedWeeksToCompletion > 1 ? 's' : ''}`;
    } else {
      const months = Math.ceil(adjustedWeeksToCompletion / 4);
      estimatedTime = `~${months} month${months > 1 ? 's' : ''}`;
    }
    
    return { 
      certCount, 
      courseCount, 
      totalCost, 
      completedCount, 
      totalCount: items.length,
      estimatedTime,
      weeksToCompletion: adjustedWeeksToCompletion,
      hoursPerWeek,
      velocity: velocity.toFixed(2),
      hireReadyWindow,
      weeksElapsed: weeksElapsed.toFixed(1)
    };
  };

  // Filter work history to last 10 years
  const getFilteredWorkHistory = () => {
    // Fallback to experience if workHistory is empty
    const workHistory = (profile.workHistory?.length > 0 ? profile.workHistory : profile.experience) || [];
    console.log('📋 Work history data:', workHistory);
    
    const tenYearsAgo = new Date();
    tenYearsAgo.setFullYear(tenYearsAgo.getFullYear() - 10);
    
    return workHistory.filter(job => {
      if (!job.endDate && job.current) return true; // Current job always included
      // Parse various date formats
      const endDateStr = job.endDate || job.end;
      if (!endDateStr) return true; // No end date = current job
      const endDate = new Date(endDateStr);
      if (isNaN(endDate.getTime())) return true; // Invalid date = include
      return endDate >= tenYearsAgo;
    }).slice(0, showAllWorkHistory ? undefined : 3);
  };

  // Get status icon
  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return <CompletedIcon sx={{ color: '#22c55e' }} />;
      case 'in_progress':
        return <InProgressIcon sx={{ color: '#f59e0b' }} />;
      default:
        return <NotStartedIcon sx={{ color: '#6b7280' }} />;
    }
  };

  // Get next status
  const getNextStatus = (currentStatus) => {
    const statusOrder = ['not_started', 'in_progress', 'completed'];
    const currentIndex = statusOrder.indexOf(currentStatus);
    return statusOrder[(currentIndex + 1) % statusOrder.length];
  };

  if (loading && !generating) {
    return (
      <Box sx={{ 
        minHeight: '100vh', 
        bgcolor: '#0a0a0a', 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center' 
      }}>
        <CircularProgress sx={{ color: '#00FFF5' }} />
      </Box>
    );
  }

  // Generating state - show progress while report is being built
  if (generating || (loading && pollCount > 0)) {
    return (
      <Box sx={{ 
        minHeight: '100vh', 
        bgcolor: '#0a0a0a', 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center',
        px: 4
      }}>
        <GeneratingProgress 
          targetRole={profile?.targetRole || 'your target role'} 
          startTime={generatingStartTime || Date.now()} 
        />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: '#0a0a0a', py: 4 }}>
        <Container maxWidth="lg">
          <Button
            startIcon={<BackIcon />}
            onClick={() => navigate(-1)}
            sx={{ mb: 3, color: '#00FFF5' }}
          >
            Back
          </Button>
          <Alert severity="error" sx={{ bgcolor: '#1f1f1f', color: '#f87171' }}>
            {error}
          </Alert>
        </Container>
      </Box>
    );
  }

  if (!profile) {
    return null;
  }

  const progressPercentage = profile.trainingPlan?.summary?.percentage || 0;

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#0a0a0a', py: 4 }}>
      <Container maxWidth="lg">
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
          <Button
            startIcon={<BackIcon />}
            onClick={() => navigate('/dashboard')}
            sx={{ color: '#00FFF5' }}
          >
            Back to Dashboard
          </Button>
          
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Tooltip title="Copy profile link">
              <IconButton onClick={handleCopyLink} sx={{ color: '#a1a1aa' }}>
                <ShareIcon />
              </IconButton>
            </Tooltip>
            
            {isOwner && (
              <Button
                variant="outlined"
                startIcon={editMode ? <PreviewIcon /> : <EditIcon />}
                onClick={() => setEditMode(!editMode)}
                sx={{ 
                  color: '#00FFF5', 
                  borderColor: '#00FFF5',
                  '&:hover': { borderColor: '#00FFF5', bgcolor: 'rgba(0,255,245,0.1)' }
                }}
              >
                {editMode ? 'Preview' : 'Edit'}
              </Button>
            )}
          </Box>
        </Box>

        {/* Mode Banner */}
        {isOwner && (
          <Alert 
            severity="info" 
            sx={{ 
              mb: 3, 
              bgcolor: editMode ? 'rgba(0,255,245,0.1)' : 'rgba(157,0,255,0.1)',
              border: `1px solid ${editMode ? '#00FFF5' : '#9D00FF'}`,
              color: '#fafafa',
              '& .MuiAlert-icon': { color: editMode ? '#00FFF5' : '#9D00FF' }
            }}
          >
            {editMode 
              ? 'Edit Mode: Make changes to your profile and training progress'
              : 'Preview Mode: This is how others see your public profile'
            }
          </Alert>
        )}

        {/* Top Save Button - visible in edit mode */}
        {editMode && (
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 3 }}>
            <Button
              variant="contained"
              onClick={handleSave}
              disabled={saving}
              sx={{ 
                bgcolor: '#00FFF5', 
                color: '#0a0a0a',
                '&:hover': { bgcolor: '#00CCC5' },
                px: 4
              }}
            >
              {saving ? <CircularProgress size={20} /> : 'Save Changes'}
            </Button>
          </Box>
        )}

        {/* Profile Header Card */}
        <Paper sx={{ 
          p: 4, 
          mb: 4, 
          bgcolor: '#171717', 
          border: '1px solid #262626',
          borderRadius: '12px'
        }}>
          <Grid container spacing={4} alignItems="center">
            {/* Profile Photo */}
            <Grid item xs={12} md={2}>
              <Box sx={{ position: 'relative', display: 'flex', justifyContent: 'center' }}>
                <Box
                  sx={{
                    width: 100,
                    height: 100,
                    borderRadius: '50%',
                    overflow: 'hidden',
                    border: '3px solid #00FFF5',
                    bgcolor: '#262626',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  {profile.photoUrl ? (
                    <Box
                      component="img"
                      src={profile.photoUrl}
                      alt="Profile"
                      sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                  ) : (
                    <Typography variant="h3" sx={{ color: '#00FFF5' }}>
                      {(profile.name || profile.currentRole || 'P')[0].toUpperCase()}
                    </Typography>
                  )}
                </Box>
                {isOwner && editMode && (
                  <Box sx={{ position: 'absolute', bottom: 0, right: '50%', transform: 'translateX(50%)' }}>
                    <input
                      accept="image/*"
                      style={{ display: 'none' }}
                      id="photo-upload"
                      type="file"
                      onChange={handlePhotoUpload}
                    />
                    <label htmlFor="photo-upload">
                      <IconButton
                        component="span"
                        disabled={uploadingPhoto}
                        sx={{
                          bgcolor: '#00FFF5',
                          color: '#0a0a0a',
                          '&:hover': { bgcolor: '#00CCC5' },
                          width: 32,
                          height: 32
                        }}
                      >
                        {uploadingPhoto ? <CircularProgress size={16} /> : <PhotoIcon sx={{ fontSize: 16 }} />}
                      </IconButton>
                    </label>
                  </Box>
                )}
              </Box>
            </Grid>

            {/* User Info */}
            <Grid item xs={12} md={5}>
              {editMode ? (
                <Box>
                  <TextField
                    fullWidth
                    label="Current Role"
                    value={editedFields.currentRole}
                    onChange={(e) => setEditedFields(prev => ({ ...prev, currentRole: e.target.value }))}
                    sx={{ mb: 2 }}
                    InputProps={{ sx: { color: '#fafafa' } }}
                    InputLabelProps={{ sx: { color: '#a1a1aa' } }}
                  />
                  <TextField
                    fullWidth
                    label="Target Role"
                    value={editedFields.targetRole}
                    onChange={(e) => setEditedFields(prev => ({ ...prev, targetRole: e.target.value }))}
                    sx={{ mb: 2 }}
                    InputProps={{ sx: { color: '#fafafa' } }}
                    InputLabelProps={{ sx: { color: '#a1a1aa' } }}
                  />
                  <TextField
                    fullWidth
                    label="Years of Experience"
                    type="number"
                    value={editedFields.yearsExperience}
                    onChange={(e) => setEditedFields(prev => ({ ...prev, yearsExperience: parseInt(e.target.value) || 0 }))}
                    InputProps={{ sx: { color: '#fafafa' } }}
                    InputLabelProps={{ sx: { color: '#a1a1aa' } }}
                  />
                </Box>
              ) : (
                <Box>
                  {/* Name as main heading */}
                  <Typography variant="h4" sx={{ color: '#fafafa', fontWeight: 700, mb: 0.5 }}>
                    {profile.name || 'Career Professional'}
                  </Typography>
                  {/* Current role below name */}
                  <Typography variant="h6" sx={{ color: '#a1a1aa', fontWeight: 500, mb: 1.5 }}>
                    {profile.currentRole || 'Professional'}
                  </Typography>
                  {/* Target role with updated wording */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Typography variant="body1" sx={{ color: '#a1a1aa' }}>Qualifying for:</Typography>
                    <Typography variant="body1" sx={{ color: '#00FFF5', fontWeight: 600 }}>
                      {profile.targetRole || 'Target Role'}
                    </Typography>
                  </Box>
                  <Typography variant="body2" sx={{ color: '#6b7280' }}>
                    {profile.yearsExperience || 0} years of experience
                  </Typography>
                </Box>
              )}
            </Grid>

            {/* Right: Cognitive Metrics */}
            <Grid item xs={12} md={5}>
              {loadingCognitive ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                  <CircularProgress size={24} sx={{ color: '#9D00FF' }} />
                </Box>
              ) : cognitiveData?.hasReport ? (
                /* Unlocked State - Premium Cognitive Profile - Compact Horizontal Layout */
                <Box sx={{ 
                  display: 'flex', 
                  gap: 2, 
                  width: '100%',
                  alignItems: 'stretch'
                }}>
                  {/* Intelligence Card */}
                  <Tooltip 
                    title="Measures analytical reasoning complexity, abstraction level, and problem-solving sophistication based on your communication patterns."
                    arrow 
                    placement="top"
                  >
                    <Box sx={{ 
                      flex: '0 0 140px',
                      p: 1.5,
                      bgcolor: 'rgba(157, 0, 255, 0.05)',
                      borderRadius: '10px',
                      border: '1px solid rgba(157, 0, 255, 0.2)',
                      textAlign: 'center',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center',
                      cursor: 'help'
                    }}>
                      <Typography variant="overline" sx={{ color: '#9D00FF', fontWeight: 600, letterSpacing: 1.5, fontSize: '0.6rem', display: 'block' }}>
                        INTELLIGENCE
                      </Typography>
                      <Typography variant="h4" sx={{ color: '#fafafa', fontWeight: 700, lineHeight: 1, my: 0.5 }}>
                        {cognitiveData.ces?.score || '—'}
                      </Typography>
                      {(() => {
                        const percentile = 100 - (cognitiveData.ces?.percentile || 50);
                        let tierLabel = 'Capable';
                        let tierColor = '#6b7280';
                        if (percentile <= 1) { tierLabel = 'Distinguished'; tierColor = '#D4AF37'; }
                        else if (percentile <= 5) { tierLabel = 'Exceptional'; tierColor = '#22c55e'; }
                        else if (percentile <= 10) { tierLabel = 'Advanced'; tierColor = '#06b6d4'; }
                        else if (percentile <= 25) { tierLabel = 'Proficient'; tierColor = '#f59e0b'; }
                        return (
                          <>
                            <Chip
                              label={tierLabel.toUpperCase()}
                              size="small"
                              sx={{
                                bgcolor: `${tierColor}20`,
                                color: tierColor,
                                fontWeight: 700,
                                fontSize: '0.55rem',
                                letterSpacing: 0.5,
                                height: 18,
                                mb: 0.25
                              }}
                            />
                            <Typography variant="caption" sx={{ color: '#a1a1aa', fontSize: '0.6rem' }}>
                              Top {percentile}%
                            </Typography>
                          </>
                        );
                      })()}
                    </Box>
                  </Tooltip>
                  
                  {/* Creativity Card */}
                  <Tooltip 
                    title="Measures creative thinking across four dimensions: idea generation rate, novelty of connections, depth of concept development, and ability to shift between domains."
                    arrow 
                    placement="top"
                  >
                    <Box sx={{ 
                      flex: '0 0 140px',
                      p: 1.5,
                      bgcolor: 'rgba(157, 0, 255, 0.05)',
                      borderRadius: '10px',
                      border: '1px solid rgba(157, 0, 255, 0.2)',
                      textAlign: 'center',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center',
                      cursor: 'help'
                    }}>
                      <Typography variant="overline" sx={{ color: '#9D00FF', fontWeight: 600, letterSpacing: 1.5, fontSize: '0.6rem', display: 'block' }}>
                        CREATIVITY
                      </Typography>
                      <Typography variant="h4" sx={{ color: '#fafafa', fontWeight: 700, lineHeight: 1, my: 0.5 }}>
                        {cognitiveData.cq?.composite || '—'}
                        <Typography component="span" sx={{ fontSize: '0.4em', color: '#737373' }}>/100</Typography>
                      </Typography>
                      {/* Compact Gradient Bar - Brighter colors for visibility */}
                      <Box sx={{ width: '90%', mx: 'auto', height: 8, borderRadius: 4, bgcolor: '#1a1a1a', overflow: 'hidden', mb: 0.5, border: '1px solid #333' }}>
                        <Box sx={{ width: `${cognitiveData.cq?.composite || 0}%`, height: '100%', background: 'linear-gradient(90deg, #00FFF5 0%, #22c55e 100%)', borderRadius: 4 }} />
                      </Box>
                      {(() => {
                        const score = cognitiveData.cq?.composite || 0;
                        let tierLabel = 'Emerging';
                        let tierColor = '#6b7280';
                        if (score >= 90) { tierLabel = 'Excellent'; tierColor = '#22c55e'; }
                        else if (score >= 75) { tierLabel = 'Strong'; tierColor = '#06b6d4'; }
                        else if (score >= 60) { tierLabel = 'Developing'; tierColor = '#f59e0b'; }
                        return (
                          <Chip label={tierLabel.toUpperCase()} size="small" sx={{ bgcolor: `${tierColor}20`, color: tierColor, fontWeight: 700, fontSize: '0.55rem', height: 18 }} />
                        );
                      })()}
                    </Box>
                  </Tooltip>
                  
                  {/* Creativity Dimensions - Compact 2x2 Grid */}
                  <Box sx={{ 
                    flex: 1,
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr',
                    gridTemplateRows: '1fr 1fr',
                    gap: 1,
                    p: 1,
                    bgcolor: 'rgba(255, 255, 255, 0.02)',
                    borderRadius: '10px',
                    border: '1px solid rgba(255, 255, 255, 0.05)'
                  }}>
                    {[
                      { key: 'fluency', label: 'Fluency', tooltip: 'Rate of idea generation - how many unique concepts you produce' },
                      { key: 'originality', label: 'Originality', tooltip: 'Novelty of connections - how unexpected and unique your thinking patterns are' },
                      { key: 'elaboration', label: 'Elaboration', tooltip: 'Depth of reasoning - how thoroughly you develop and explain concepts' },
                      { key: 'flexibility', label: 'Flexibility', tooltip: 'Perspective shifting - your ability to adapt across different domains' }
                    ].map((dim) => {
                      const score = cognitiveData.cq?.[dim.key] || 0;
                      let tierColor = '#6b7280';
                      if (score >= 90) { tierColor = '#22c55e'; }
                      else if (score >= 75) { tierColor = '#06b6d4'; }
                      else if (score >= 60) { tierColor = '#f59e0b'; }
                      
                      return (
                        <Tooltip key={dim.key} title={dim.tooltip} arrow placement="top">
                          <Box sx={{ 
                            display: 'flex', 
                            flexDirection: 'column',
                            px: 1,
                            py: 0.5,
                            bgcolor: 'rgba(255,255,255,0.02)',
                            borderRadius: '6px',
                            cursor: 'help'
                          }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.25 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <Typography variant="caption" sx={{ color: '#a1a1aa', fontSize: '0.7rem' }}>
                                  {dim.label}
                                </Typography>
                                <InfoIcon sx={{ fontSize: 10, color: '#6b7280' }} />
                              </Box>
                              <Typography variant="body2" sx={{ color: tierColor, fontWeight: 700 }}>
                                {score}<span style={{ fontSize: '0.7em', color: '#6b7280', fontWeight: 400 }}>/100</span>
                              </Typography>
                            </Box>
                            {/* Mini progress bar */}
                            <Box sx={{ width: '100%', height: 3, borderRadius: 2, bgcolor: '#262626', overflow: 'hidden' }}>
                              <Box sx={{ width: `${score}%`, height: '100%', bgcolor: tierColor, borderRadius: 2 }} />
                            </Box>
                          </Box>
                        </Tooltip>
                      );
                    })}
                  </Box>
                </Box>
              ) : (
                /* Locked State - Compact Horizontal Blurred Teaser */
                <Box sx={{ 
                  position: 'relative',
                  bgcolor: 'rgba(157, 0, 255, 0.03)', 
                  borderRadius: '10px',
                  border: '1px solid rgba(157, 0, 255, 0.3)',
                  overflow: 'hidden',
                  width: '100%',
                  height: '100%',
                  minHeight: 120
                }}>
                  {/* Blurred Sample - Horizontal Layout */}
                  <Box sx={{ 
                    display: 'flex',
                    gap: 2,
                    p: 1.5,
                    filter: 'blur(3px)',
                    opacity: 0.4,
                    pointerEvents: 'none'
                  }}>
                    <Box sx={{ flex: '0 0 100px', textAlign: 'center', p: 1, bgcolor: 'rgba(157, 0, 255, 0.1)', borderRadius: '8px' }}>
                      <Typography variant="overline" sx={{ color: '#9D00FF', fontSize: '0.5rem' }}>INTELLIGENCE</Typography>
                      <Typography variant="h5" sx={{ color: '#fafafa', fontWeight: 700 }}>138</Typography>
                      <Chip label="DISTINGUISHED" size="small" sx={{ bgcolor: 'rgba(212, 175, 55, 0.2)', color: '#D4AF37', fontSize: '0.5rem', height: 16 }} />
                    </Box>
                    <Box sx={{ flex: '0 0 100px', textAlign: 'center', p: 1, bgcolor: 'rgba(157, 0, 255, 0.1)', borderRadius: '8px' }}>
                      <Typography variant="overline" sx={{ color: '#9D00FF', fontSize: '0.5rem' }}>CREATIVITY</Typography>
                      <Typography variant="h5" sx={{ color: '#fafafa', fontWeight: 700 }}>92</Typography>
                      <Chip label="EXCELLENT" size="small" sx={{ bgcolor: 'rgba(34, 197, 94, 0.2)', color: '#22c55e', fontSize: '0.5rem', height: 16 }} />
                    </Box>
                    <Box sx={{ flex: 1, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0.5, p: 1 }}>
                      {['Fluency 94', 'Originality 90', 'Elaboration 87', 'Flexibility 97'].map((dim) => (
                        <Typography key={dim} variant="caption" sx={{ color: '#a1a1aa', fontSize: '0.6rem' }}>{dim}</Typography>
                      ))}
                    </Box>
                  </Box>
                  
                  {/* Overlay with Lock */}
                  <Box sx={{ 
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 2,
                    bgcolor: 'rgba(0, 0, 0, 0.6)',
                    backdropFilter: 'blur(2px)'
                  }}>
                    <LockIcon sx={{ fontSize: 28, color: '#9D00FF' }} />
                    <Box sx={{ textAlign: 'left' }}>
                      <Typography variant="body1" sx={{ color: '#fafafa', fontWeight: 600, lineHeight: 1.2 }}>
                        Cognitive Profile
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#a1a1aa' }}>
                        Stand out to employers
                      </Typography>
                    </Box>
                    <Button
                      variant="contained"
                      size="small"
                      onClick={() => navigate('/')}
                      sx={{
                        bgcolor: '#9D00FF',
                        color: '#fafafa',
                        fontSize: '0.8rem',
                        fontWeight: 600,
                        px: 2.5,
                        py: 0.75,
                        ml: 1,
                        '&:hover': { bgcolor: '#7D00CC' }
                      }}
                    >
                      Unlock – ${platformSettings?.reportPrice || 29}
                    </Button>
                  </Box>
                </Box>
              )}
            </Grid>

          </Grid>

        </Paper>

        {/* Qualified Roles Section - Shows roles the user has completed training for */}
        {profile.qualifiedRoles && profile.qualifiedRoles.length > 0 && (
          <Paper sx={{ 
            p: 4, 
            mb: 4, 
            bgcolor: '#171717', 
            border: '2px solid #fbbf24',
            borderRadius: '12px',
            background: 'linear-gradient(135deg, rgba(251, 191, 36, 0.05) 0%, rgba(23, 23, 23, 1) 100%)'
          }}>
            <Typography variant="h6" sx={{ 
              color: '#fbbf24', 
              fontWeight: 700, 
              display: 'flex', 
              alignItems: 'center', 
              gap: 1, 
              mb: 2 
            }}>
              🏆 Qualified Roles
            </Typography>
            <Typography variant="body2" sx={{ color: '#a1a1aa', mb: 3 }}>
              Roles I've completed training programs for and am ready to pursue
            </Typography>
            
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {profile.qualifiedRoles.map((qual, index) => (
                <Box 
                  key={index}
                  sx={{ 
                    p: 2,
                    bgcolor: 'rgba(251, 191, 36, 0.1)',
                    borderRadius: '8px',
                    border: '1px solid rgba(251, 191, 36, 0.3)'
                  }}
                >
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                    <Typography variant="subtitle1" sx={{ color: '#fafafa', fontWeight: 600 }}>
                      {qual.role}
                    </Typography>
                    <Chip 
                      label="QUALIFIED" 
                      size="small"
                      sx={{ 
                        bgcolor: 'rgba(251, 191, 36, 0.2)',
                        color: '#fbbf24',
                        fontWeight: 700,
                        fontSize: '0.65rem'
                      }}
                    />
                  </Box>
                  <Typography variant="caption" sx={{ color: '#6b7280', display: 'block' }}>
                    Completed: {qual.completedAt ? new Date(qual.completedAt).toLocaleDateString() : 'N/A'}
                    {qual.fromRole && ` • From: ${qual.fromRole}`}
                  </Typography>
                  {qual.certsGained && qual.certsGained.length > 0 && (
                    <Box sx={{ mt: 1, display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                      {qual.certsGained.map((cert, cIdx) => (
                        <Chip 
                          key={cIdx}
                          label={`★ ${cert}`}
                          size="small"
                          sx={{ 
                            bgcolor: 'rgba(212, 175, 55, 0.2)',
                            color: '#D4AF37',
                            fontSize: '0.7rem'
                          }}
                        />
                      ))}
                    </Box>
                  )}
                  {qual.coursesCompleted > 0 && (
                    <Typography variant="caption" sx={{ color: '#a1a1aa', display: 'block', mt: 1 }}>
                      {qual.coursesCompleted} courses completed
                    </Typography>
                  )}
                </Box>
              ))}
            </Box>
          </Paper>
        )}

        {/* Background Sections Toggle */}
        <Paper sx={{ 
          p: 2, 
          mb: showBackgroundSections ? 2 : 4, 
          bgcolor: '#171717', 
          border: '1px solid #262626',
          borderRadius: '12px',
          cursor: 'pointer',
          transition: 'all 0.2s ease',
          '&:hover': { borderColor: '#404040' }
        }}
        onClick={() => setShowBackgroundSections(!showBackgroundSections)}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <WorkIcon sx={{ color: '#00FFF5' }} />
              <Typography variant="subtitle1" sx={{ color: '#fafafa', fontWeight: 500 }}>
                Background & Experience
              </Typography>
              <Typography variant="caption" sx={{ color: '#6b7280' }}>
                Work History, Education, Certifications, Skills
              </Typography>
            </Box>
            <IconButton size="small" sx={{ color: '#a1a1aa' }}>
              {showBackgroundSections ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          </Box>
        </Paper>

        {/* Collapsible Background Sections */}
        {showBackgroundSections && (
          <>
            {/* Work History Section */}
            <Paper sx={{ 
              p: 4, 
              mb: 4, 
              bgcolor: '#171717', 
              border: '1px solid #262626',
              borderRadius: '12px'
            }}>
              <Typography variant="h6" sx={{ color: '#fafafa', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                <WorkIcon sx={{ color: '#00FFF5' }} />
                Work History
              </Typography>

          {getFilteredWorkHistory().length > 0 ? (
            <>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                {getFilteredWorkHistory().map((job, index) => (
                  <Box key={index} sx={{ 
                    pl: 3, 
                    borderLeft: `2px solid ${job.current ? '#00FFF5' : '#404040'}` 
                  }}>
                    <Typography variant="subtitle1" sx={{ color: '#fafafa', fontWeight: 600 }}>
                      {job.title || job.position || job.role || 'Position'}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#00FFF5' }}>
                      {job.company || job.organization || job.employer || 'Company'}
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#6b7280', display: 'block', mb: 1 }}>
                      {job.startDate || job.start || 'Start'} - {job.current ? 'Present' : (job.endDate || job.end || 'End')}
                    </Typography>
                    
                    {/* Display highlights as bullet points */}
                    {job.highlights && job.highlights.length > 0 ? (
                      <Box component="ul" sx={{ m: 0, pl: 2, mt: 1 }}>
                        {job.highlights.map((highlight, hIdx) => (
                          <Box 
                            component="li" 
                            key={hIdx}
                            sx={{ 
                              color: '#a1a1aa', 
                              fontSize: '0.875rem',
                              mb: 0.5,
                              '&::marker': { color: '#00FFF5' }
                            }}
                          >
                            {typeof highlight === 'string' ? highlight : ''}
                          </Box>
                        ))}
                      </Box>
                    ) : job.description && (
                      <Typography variant="body2" sx={{ color: '#a1a1aa', mt: 1 }}>
                        {typeof job.description === 'string' ? job.description : ''}
                      </Typography>
                    )}
                  </Box>
                ))}
              </Box>

              {/* Earlier Career Summary */}
              {profile.earlierCareerSummary && (
                <Box sx={{ 
                  mt: 4, 
                  pt: 3, 
                  borderTop: '1px solid #262626'
                }}>
                  <Typography variant="subtitle2" sx={{ color: '#9D00FF', mb: 1.5, fontWeight: 600 }}>
                    Earlier Experience
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#a1a1aa', lineHeight: 1.7, fontStyle: 'italic' }}>
                    {profile.earlierCareerSummary}
                  </Typography>
                </Box>
              )}

              {/* Show More Work History */}
              {((profile.workHistory?.length || 0) + (profile.experience?.length || 0)) > 3 && (
                <Box sx={{ textAlign: 'center', mt: 3 }}>
                  <Button
                    onClick={() => setShowAllWorkHistory(!showAllWorkHistory)}
                    endIcon={showAllWorkHistory ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    sx={{ color: '#a1a1aa' }}
                  >
                    {showAllWorkHistory ? 'Show Less' : 'Show Full History'}
                  </Button>
                </Box>
              )}
            </>
          ) : (
            <Typography variant="body2" sx={{ color: '#6b7280', textAlign: 'center', py: 2 }}>
              No work history available. Your work experience will appear here after generating a career report.
            </Typography>
          )}
        </Paper>

        {/* Education Section */}
        {profile.education && profile.education.length > 0 && (
          <Paper sx={{ 
            p: 4, 
            mb: 4, 
            bgcolor: '#171717', 
            border: '1px solid #262626',
            borderRadius: '12px'
          }}>
            <Typography variant="h6" sx={{ color: '#fafafa', fontWeight: 600, mb: 3 }}>
              Education
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {profile.education.map((edu, index) => {
                // Handle both string and object formats
                const eduData = typeof edu === 'string' 
                  ? { degree: edu } 
                  : edu;
                return (
                  <Box key={index} sx={{ 
                    pl: 3, 
                    borderLeft: '2px solid #9D00FF'
                  }}>
                    <Typography variant="subtitle1" sx={{ color: '#fafafa', fontWeight: 600 }}>
                      {eduData.degree || eduData.name || edu}
                    </Typography>
                    {eduData.major && (
                      <Typography variant="body2" sx={{ color: '#00FFF5' }}>
                        {eduData.major}
                      </Typography>
                    )}
                    {eduData.school && (
                      <Typography variant="body2" sx={{ color: '#a1a1aa' }}>
                        {eduData.school}
                      </Typography>
                    )}
                    {eduData.graduationYear && (
                      <Typography variant="caption" sx={{ color: '#6b7280' }}>
                        {eduData.graduationYear}
                      </Typography>
                    )}
                  </Box>
                );
              })}
            </Box>
          </Paper>
        )}

        {/* Certifications Section */}
        {profile.certifications && profile.certifications.length > 0 && (
          <Paper sx={{ 
            p: 4, 
            mb: 4, 
            bgcolor: '#171717', 
            border: '1px solid #262626',
            borderRadius: '12px'
          }}>
            <Typography variant="h6" sx={{ color: '#fafafa', fontWeight: 600, mb: 3 }}>
              Certifications
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {profile.certifications.map((cert, index) => {
                // Handle both string and object formats
                const certName = typeof cert === 'string' 
                  ? cert 
                  : (cert.name || cert.certification || String(cert));
                return (
                  <Chip
                    key={index}
                    label={certName}
                    sx={{
                      bgcolor: 'rgba(212,175,55,0.15)',
                      color: '#D4AF37',
                      border: '1px solid #D4AF37',
                      fontWeight: 500,
                      '& .MuiChip-label': { px: 2 }
                    }}
                  />
                );
              })}
            </Box>
          </Paper>
        )}

        {/* Skills Section */}
        <Paper sx={{ 
          p: 4, 
          mb: 4, 
          bgcolor: '#171717', 
          border: '1px solid #262626',
          borderRadius: '12px'
        }}>
          <Typography variant="h6" sx={{ color: '#fafafa', fontWeight: 600, mb: 3 }}>
            Acquired Skills
          </Typography>
          
          {editMode && (
            <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
              <TextField
                size="small"
                placeholder="Add a skill..."
                value={skillInput}
                onChange={(e) => setSkillInput(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleAddSkill()}
                sx={{ flex: 1 }}
                InputProps={{ sx: { color: '#fafafa' } }}
              />
              <Button
                variant="outlined"
                onClick={handleAddSkill}
                sx={{ color: '#00FFF5', borderColor: '#00FFF5' }}
              >
                Add
              </Button>
            </Box>
          )}

          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {(editMode ? editedFields.skills : profile.skills || []).map((skill, index) => {
              // Handle both string skills and object skills {skill, importance, frequency}
              const skillName = typeof skill === 'object' ? (skill.skill || skill.name || JSON.stringify(skill)) : skill;
              return (
                <Chip
                  key={index}
                  label={skillName}
                  onDelete={editMode ? () => handleRemoveSkill(skill) : undefined}
                  sx={{
                    bgcolor: '#262626',
                    color: '#fafafa',
                    border: '1px solid #404040',
                    '& .MuiChip-deleteIcon': { color: '#a1a1aa' }
                  }}
                />
              );
            })}
            {(!editMode && (!profile.skills || profile.skills.length === 0)) && (
              <Typography variant="body2" sx={{ color: '#6b7280' }}>
                No skills listed
              </Typography>
            )}
          </Box>
        </Paper>
          </>
        )}

        {/* Gap Analysis - Shows what skills are needed for target role */}
        {profile.gapAnalysis && (
          <Paper sx={{ 
            p: 4, 
            mb: 4, 
            bgcolor: '#171717', 
            border: '1px solid #262626',
            borderRadius: '12px'
          }}>
            {/* Header with Qualification Score */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
              <Box>
                <Typography variant="h6" sx={{ color: '#fafafa', fontWeight: 600, mb: 1 }}>
                  Gap Analysis{profile.targetRole ? `: ${profile.targetRole}` : ''}
                </Typography>
                <Typography variant="body2" sx={{ color: '#a1a1aa' }}>
                  Skills comparison between your current experience and target role requirements
                </Typography>
              </Box>
              
              {/* Qualification Score */}
              <Box sx={{ textAlign: 'center', minWidth: 100 }}>
                <Box sx={{ 
                  position: 'relative', 
                  display: 'inline-flex',
                  mb: 1
                }}>
                  <CircularProgress
                    variant="determinate"
                    value={profile.qualificationPercentage || 0}
                    size={80}
                    thickness={4}
                    sx={{ 
                      color: profile.qualificationPercentage >= 70 ? '#22c55e' : 
                             profile.qualificationPercentage >= 40 ? '#f59e0b' : '#ef4444'
                    }}
                  />
                  <Box sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    bottom: 0,
                    right: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}>
                    <Typography variant="h5" sx={{ color: '#fafafa', fontWeight: 700 }}>
                      {profile.qualificationPercentage || 0}%
                    </Typography>
                  </Box>
                </Box>
                <Typography variant="caption" sx={{ color: '#a1a1aa', display: 'block' }}>
                  Qualified
                </Typography>
              </Box>
            </Box>
            
            <Grid container spacing={3}>
              {/* Matched Skills */}
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" sx={{ color: '#22c55e', mb: 2 }}>
                  Matched Skills
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {(profile.gapAnalysis.matchedSkills || []).slice(0, 10).map((skill, i) => {
                    const skillName = typeof skill === 'object' ? (skill.skill || skill.name || String(skill)) : skill;
                    return (
                      <Chip
                        key={i}
                        label={skillName}
                        size="small"
                        sx={{ bgcolor: 'rgba(34,197,94,0.2)', color: '#22c55e', border: '1px solid #22c55e' }}
                      />
                    );
                  })}
                </Box>
              </Grid>

              {/* Missing Skills */}
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" sx={{ color: '#f59e0b', mb: 2 }}>
                  Skills to Develop
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {(profile.gapAnalysis.missingSkills || []).slice(0, 10).map((skill, i) => {
                    const skillName = typeof skill === 'object' ? (skill.skill || skill.name || String(skill)) : skill;
                    return (
                      <Chip
                        key={i}
                        label={skillName}
                        size="small"
                        sx={{ bgcolor: 'rgba(245,158,11,0.2)', color: '#f59e0b', border: '1px solid #f59e0b' }}
                      />
                    );
                  })}
                </Box>
              </Grid>
            </Grid>
          </Paper>
        )}

        {/* Training Plan - Simple Checklist */}
        <Paper sx={{ 
          p: 4, 
          mb: 4, 
          bgcolor: '#171717', 
          border: '1px solid #262626',
          borderRadius: '12px'
        }}>
          {/* Header with stats */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
            <Box>
              <Typography variant="h6" sx={{ color: '#fafafa', fontWeight: 600, mb: 0.5 }}>
                Training Plan for {profile.targetRole || 'Target Role'}
              </Typography>
              <Typography variant="body2" sx={{ color: '#a1a1aa', mb: 1.5, maxWidth: 500 }}>
                A job market data-driven plan based on common employer requirements for this role.
              </Typography>
              {profile.trainingPlan?.items?.length > 0 && (
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                  <Typography variant="caption" sx={{ color: '#a1a1aa' }}>
                    {getTrainingStats().courseCount} courses
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#D4AF37' }}>
                    {getTrainingStats().certCount} certifications
                  </Typography>
                  {/* Only show estimated cost in edit mode */}
                  {isOwner && editMode && (
                    <Typography variant="caption" sx={{ color: '#22c55e' }}>
                      Est. ${getTrainingStats().totalCost}
                    </Typography>
                  )}
                </Box>
              )}
            </Box>
            <Box sx={{ textAlign: 'right' }}>
              <Chip
                label={`${getTrainingStats().completedCount}/${getTrainingStats().totalCount} Complete`}
                sx={{ 
                  bgcolor: progressPercentage >= 100 ? '#22c55e' : '#00FFF5',
                  color: '#0a0a0a',
                  fontWeight: 600,
                  mb: 0.5
                }}
              />
              {getTrainingStats().weeksToCompletion > 0 && (
                <Typography variant="caption" sx={{ color: '#a1a1aa', display: 'block' }}>
                  Est. {getTrainingStats().estimatedTime} @ {getTrainingStats().hoursPerWeek} hrs/wk
                </Typography>
              )}
            </Box>
          </Box>

          {/* Progress Bar */}
          <Box sx={{ mb: 4 }}>
            <LinearProgress
              variant="determinate"
              value={progressPercentage}
              sx={{
                height: 8,
                borderRadius: 4,
                bgcolor: '#262626',
                '& .MuiLinearProgress-bar': {
                  bgcolor: progressPercentage >= 100 ? '#22c55e' : '#00FFF5',
                  borderRadius: 4
                }
              }}
            />
          </Box>

          {/* Simple Flat Checklist */}
          {(!profile.trainingPlan?.items || profile.trainingPlan.items.length === 0) ? (
            <Typography variant="body2" sx={{ color: '#6b7280', textAlign: 'center', py: 4 }}>
              No training plan items yet. Generate a career report to get your personalized plan.
            </Typography>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {(() => {
                // Track displayed membership providers to show cost only once
                const displayedMembershipProviders = new Set();
                
                // Subscription-based providers (show cost once per provider as "$X/mo")
                const subscriptionProviders = [
                  'coursera', 'linkedin learning', 'linkedin', 'pluralsight', 
                  'a cloud guru', 'acloudguru', 'udacity', 'skillshare',
                  'datacamp', 'codecademy', 'treehouse', 'masterclass'
                ];
                
                const isSubscriptionProvider = (provider) => {
                  const providerLower = (provider || '').toLowerCase();
                  return subscriptionProviders.some(sub => providerLower.includes(sub));
                };
                
                return getDisplayedTrainingItems().map((item) => {
                  const isCertification = item.type === 'certification';
                  
                  // Safely extract name - handle nested objects from legacy data
                  const extractName = () => {
                    if (typeof item.name === 'string') return item.name;
                    if (typeof item.name === 'object' && item.name !== null) {
                      return item.name.courseName || item.name.skillName || item.name.name || 'Training Item';
                    }
                    if (typeof item.title === 'string') return item.title;
                    if (typeof item.title === 'object' && item.title !== null) {
                      return item.title.courseName || item.title.skillName || item.title.task || 'Training Item';
                    }
                    return 'Training Item';
                  };
                  
                  // Safely extract other fields - handle nested objects
                  const extractString = (value) => {
                    if (!value) return '';
                    if (typeof value === 'string') return value;
                    if (typeof value === 'object') {
                      return value.name || value.provider || value.url || '';
                    }
                    return String(value);
                  };
                  
                  const extractCost = (value) => {
                    if (typeof value === 'number') return value;
                    if (typeof value === 'string') {
                      const match = value.match(/\d+/);
                      return match ? parseInt(match[0]) : 0;
                    }
                    if (typeof value === 'object' && value !== null) {
                      return typeof value.cost === 'number' ? value.cost : 0;
                    }
                    return 0;
                  };
                  
                  const itemName = extractName();
                  const itemUrl = extractString(item.url) || (typeof item.name === 'object' ? item.name?.url : '') || '';
                  const itemProvider = extractString(item.provider) || (typeof item.name === 'object' ? item.name?.provider : '') || '';
                  const itemDuration = extractString(item.duration) || '';
                  const itemCost = extractCost(item.cost) || (typeof item.name === 'object' ? extractCost(item.name?.cost) : 0);
                  
                  // Determine if we should show cost for this item
                  const providerKey = itemProvider.toLowerCase();
                  const isSubscription = isSubscriptionProvider(itemProvider);
                  let showCost = false;
                  let costLabel = '';
                  
                  if (itemCost === 0) {
                    // Free items always show "Free" (in edit mode only)
                    showCost = true;
                    costLabel = 'Free';
                  } else if (isCertification) {
                    // Certifications always show their exam cost
                    showCost = true;
                    costLabel = `$${itemCost}`;
                  } else if (isSubscription) {
                    // Subscription: show cost only for first course from this provider
                    if (!displayedMembershipProviders.has(providerKey)) {
                      displayedMembershipProviders.add(providerKey);
                      showCost = true;
                      costLabel = `$${itemCost}/mo`;
                    }
                  } else {
                    // One-time purchase (e.g., Udemy) - show per item
                    showCost = true;
                    costLabel = `$${itemCost}`;
                  }
                  
                  // Certification gold styling
                  const certGold = '#D4AF37';
                  const borderColor = isCertification ? certGold : (item.status === 'completed' ? '#22c55e' : '#262626');
                  
                  // Determine if editing is allowed (owner in edit mode)
                  const canEdit = isOwner && editMode;
                  
                  return (
                    <Box
                      key={item.id}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 2,
                        p: 2,
                        bgcolor: isCertification ? 'rgba(212,175,55,0.08)' : '#1f1f1f',
                        border: `1px solid ${borderColor}`,
                        borderRadius: '8px',
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          bgcolor: isCertification ? 'rgba(212,175,55,0.12)' : '#262626'
                        }
                      }}
                    >
                      {/* Status Toggle - Only clickable for owner in edit mode */}
                      {canEdit ? (
                        <IconButton
                          onClick={() => handleTrainingItemStatusChange(item.id, getNextStatus(item.status))}
                          sx={{ 
                            p: 0.5, 
                            '&:hover': { bgcolor: 'rgba(0,255,245,0.1)' }
                          }}
                        >
                          {getStatusIcon(item.status)}
                        </IconButton>
                      ) : (
                        <Box sx={{ p: 0.5 }}>{getStatusIcon(item.status)}</Box>
                      )}
                      
                      {/* Main Content */}
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                          {/* Item Name */}
                          <Typography 
                            variant="body1" 
                            sx={{ 
                              color: item.status === 'completed' ? '#6b7280' : '#fafafa',
                              textDecoration: item.status === 'completed' ? 'line-through' : 'none',
                              fontWeight: isCertification ? 600 : 500
                            }}
                          >
                            {itemName}
                          </Typography>
                          
                          {/* Certification Badge */}
                          {isCertification && (
                            <Chip
                              label="★ CERTIFICATION"
                              size="small"
                              sx={{
                                bgcolor: certGold,
                                color: '#0a0a0a',
                                fontWeight: 700,
                                fontSize: '0.65rem',
                                height: 20
                              }}
                            />
                          )}
                        </Box>
                        
                        {/* Provider • Duration line */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5, flexWrap: 'wrap' }}>
                          {itemProvider && (
                            <Typography variant="caption" sx={{ color: isCertification ? certGold : '#00FFF5' }}>
                              {itemProvider}
                            </Typography>
                          )}
                          {itemProvider && itemDuration && (
                            <Typography variant="caption" sx={{ color: '#404040' }}>•</Typography>
                          )}
                          {itemDuration && (
                            <Typography variant="caption" sx={{ color: '#a1a1aa' }}>
                              {itemDuration}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                      
                      {/* Cost Badge - Only show in edit mode for owner */}
                      {isOwner && editMode && showCost && (
                        <Chip
                          label={costLabel}
                          size="small"
                          sx={{
                            bgcolor: costLabel === 'Free' 
                              ? 'rgba(34,197,94,0.2)' 
                              : isCertification 
                                ? 'rgba(212,175,55,0.2)' 
                                : 'rgba(34,197,94,0.2)',
                            color: costLabel === 'Free' 
                              ? '#22c55e' 
                              : isCertification 
                                ? certGold 
                                : '#22c55e',
                            fontWeight: 600,
                            height: 24,
                            minWidth: costLabel === 'Free' ? 'auto' : 50
                          }}
                        />
                      )}
                    
                    {/* Action Button */}
                    {itemUrl && (
                      <Button
                        component="a"
                        href={itemUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        size="small"
                        variant="outlined"
                        sx={{
                          color: isCertification ? certGold : '#00FFF5',
                          borderColor: isCertification ? certGold : '#00FFF5',
                          fontSize: '0.75rem',
                          px: 2,
                          whiteSpace: 'nowrap',
                          '&:hover': { 
                            bgcolor: isCertification ? 'rgba(212,175,55,0.1)' : 'rgba(0,255,245,0.1)'
                          }
                        }}
                      >
                        {isCertification 
                          ? (canEdit ? 'Schedule Exam →' : 'Exam →') 
                          : (canEdit ? 'Start Course →' : 'Course →')}
                      </Button>
                    )}
                  </Box>
                );
              });
              })()}
            </Box>
          )}

          {/* Show More/Less Button */}
          {profile.trainingPlan?.items?.length > 5 && (
            <Box sx={{ textAlign: 'center', mt: 3 }}>
              <Button
                onClick={() => setShowAllTrainingItems(!showAllTrainingItems)}
                endIcon={showAllTrainingItems ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                sx={{ color: '#00FFF5' }}
              >
                {showAllTrainingItems 
                  ? 'Show Less' 
                  : `Show All ${profile.trainingPlan.items.length} Items`
                }
              </Button>
            </Box>
          )}
        </Paper>

        {/* Bottom Save Button - visible in edit mode */}
        {editMode && (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
            <Button
              variant="contained"
              onClick={handleSave}
              disabled={saving}
              sx={{ 
                bgcolor: '#00FFF5', 
                color: '#0a0a0a',
                '&:hover': { bgcolor: '#00CCC5' },
                px: 6,
                py: 1.5
              }}
            >
              {saving ? <CircularProgress size={20} /> : 'Save All Changes'}
            </Button>
          </Box>
        )}

        {/* Download PDF */}
        {profile.pdfUrl && (
          <Box sx={{ textAlign: 'center', pb: 4 }}>
            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
              href={profile.pdfUrl}
              target="_blank"
              sx={{ 
                color: '#9D00FF', 
                borderColor: '#9D00FF',
                '&:hover': { borderColor: '#9D00FF', bgcolor: 'rgba(157,0,255,0.1)' }
              }}
            >
              Download Full Report (PDF)
            </Button>
          </Box>
        )}

        {/* Footer */}
        <Box sx={{ 
          textAlign: 'center', 
          py: 4, 
          borderTop: '1px solid #262626',
          mt: 4
        }}>
          <Typography variant="body2" sx={{ color: '#6b7280', fontFamily: 'monospace' }}>
            {platformSettings.platformName} Career Profile
          </Typography>
        </Box>
      </Container>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        message={snackbar.message}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />

      {/* Phone Prompt Modal */}
      <PhonePromptModal
        open={showPhoneModal}
        onClose={() => setShowPhoneModal(false)}
        onSave={async (phone) => {
          await updatePhone(profileId, phone);
          setProfile(prev => ({ ...prev, hasPhone: true }));
          setSnackbar({ open: true, message: 'Phone number saved!', severity: 'success' });
        }}
        existingPhone={profile?.privateData?.phone || ''}
      />
    </Box>
  );
}

export default Profile;


